import axios from "axios";

let redirectingToLogin = false;

function requestUrl(error) {
  const cfg = error.config || {};
  const base = cfg.baseURL ? String(cfg.baseURL).replace(/\/$/, "") : "";
  const path = String(cfg.url || "");
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  return base ? `${base}${path.startsWith("/") ? "" : "/"}${path}` : path;
}

/** Axios 1.x uses AxiosHeaders; bracket access is unreliable in interceptors. */
function getAuthorizationHeader(config) {
  if (!config?.headers) return "";
  const h = config.headers;
  if (typeof h.get === "function") {
    return (
      h.get("Authorization") ||
      h.get("authorization") ||
      ""
    );
  }
  return h.Authorization || h.authorization || "";
}

function hasAuthHeader(error) {
  return !!getAuthorizationHeader(error.config || {});
}

/** Metrics request with Bearer token even if config.headers shape hides it */
function isLikelyAdminMetricsWithSession(error) {
  if (!isDashboardMetricsUrl(requestUrl(error))) return false;
  try {
    return !!localStorage.getItem("adminToken");
  } catch {
    return false;
  }
}

function isAdminLoginUrl(url) {
  return url.includes("/admin/login");
}

/** Admin dashboard metrics API (e.g. …/api/admin/dashboard-metrics) */
function isDashboardMetricsUrl(url) {
  return url.includes("/admin/dashboard-metrics") || url.includes("dashboard-metrics");
}

function isServerErrorStatus(status) {
  return typeof status === "number" && status >= 500 && status < 600;
}

/** No response from server (CORS, offline, DNS, SSL, mixed content, etc.) */
function isAxiosNetworkError(error) {
  if (error?.response != null) return false;
  const code = error?.code;
  const msg = String(error?.message || "");
  return (
    code === "ERR_NETWORK" ||
    msg === "Network Error" ||
    msg.toLowerCase().includes("network error")
  );
}

axios.interceptors.response.use(
  (res) => res,
  (error) => {
    if (redirectingToLogin) return Promise.reject(error);

    const status = error.response?.status;
    const url = requestUrl(error);

    if (isAdminLoginUrl(url)) return Promise.reject(error);

    const authed =
      hasAuthHeader(error) || isLikelyAdminMetricsWithSession(error);
    if (!authed) return Promise.reject(error);

    // Dashboard metrics: network failure → same login redirect as 5xx (keep token)
    if (
      isAxiosNetworkError(error) &&
      isDashboardMetricsUrl(url)
    ) {
      redirectingToLogin = true;
      try {
        sessionStorage.setItem("adminDashboardError", "network");
      } catch {
        /* ignore */
      }
      if (typeof window !== "undefined") {
        window.location.assign(
          `${window.location.origin}/admin/login?reason=dashboard_error`,
        );
      }
      return Promise.reject(error);
    }

    // Dashboard metrics: 5xx → login with server/load error (keep session token)
    if (isServerErrorStatus(status) && isDashboardMetricsUrl(url)) {
      redirectingToLogin = true;
      try {
        sessionStorage.setItem("adminDashboardError", "server");
      } catch {
        /* ignore */
      }
      if (typeof window !== "undefined") {
        window.location.assign(
          `${window.location.origin}/admin/login?reason=dashboard_error`,
        );
      }
      return Promise.reject(error);
    }

    // 401 (any authenticated admin request except login): session expired
    if (status === 401) {
      redirectingToLogin = true;
      localStorage.removeItem("adminToken");
      try {
        sessionStorage.setItem("adminSessionExpired", "1");
      } catch {
        /* ignore */
      }
      if (typeof window !== "undefined") {
        window.location.assign(
          `${window.location.origin}/admin/login?session=expired`,
        );
      }
      return Promise.reject(error);
    }

    return Promise.reject(error);
  },
);
